import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  color,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogWindow,
  SmallText,
  space,
  Text,
  Toast,
  Toggle,
  useToast,
} from '@ticketswap/solar'
import { createMarkup, processTrackingStorage } from '~/utils'
import styled from '@emotion/styled'
import cookies from 'js-cookie'
import getConfig from 'next/config'

const {
  CONSENT_COOKIE_NAME,
  CONSENT_COOKIE_FUNCTIONAL,
  CONSENT_COOKIE_ANALYTICAL,
} = getConfig().publicRuntimeConfig

const ButtonWrapper = styled.div`
  display: grid;
  grid-gap: ${space[8]};
  grid-auto-flow: column;
  margin-top: ${space[16]};
`

const Option = styled.div`
  display: flex;
  margin-top: ${space[16]};
`

const OptionText = styled.div`
  margin-right: ${space[16]};

  span {
    display: block;

    &:nth-of-type(2) {
      color: ${color.spaceLight};
    }
  }
`

const DisabledToggle = styled(Toggle)`
  opacity: 0.3;
`

const CookieToast = ({ t }) => {
  const { notify } = useToast()
  const [notified, setNotified] = useState(false)
  const [showSettingDialog, setShowSettingDialog] = useState(false)
  const [agreedTo, setAgreedTo] = useState([
    CONSENT_COOKIE_FUNCTIONAL,
    CONSENT_COOKIE_ANALYTICAL,
  ])
  // Users who previously declined cookies via opt-out
  const cookiesDeclined = cookies.get(CONSENT_COOKIE_NAME) === 'false'
  // When functional cookies are present, the user has agreed in the past
  const hasAgreed = cookies.get(CONSENT_COOKIE_NAME)
    ? cookies.get(CONSENT_COOKIE_NAME).includes(CONSENT_COOKIE_FUNCTIONAL)
    : false
  const displayToast = !notified && !cookiesDeclined && !hasAgreed

  const handleAgreed = useCallback(() => {
    cookies.set(CONSENT_COOKIE_NAME, agreedTo, {
      expires: 365,
    })
    processTrackingStorage()
  }, [agreedTo])

  React.useEffect(() => {
    setNotified(true)

    if (displayToast) {
      notify(remove => {
        return (
          <Toast persist>
            <Text
              dangerouslySetInnerHTML={createMarkup(
                t('common:cookieNotice.text'),
                {
                  renderInline: true,
                }
              )}
            />
            <ButtonWrapper>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  setShowSettingDialog(true)
                  remove()
                }}
              >
                {t('common:generic.settings')}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  handleAgreed()
                  remove()
                }}
              >
                {t('common:generic.accept')}
              </Button>
            </ButtonWrapper>
          </Toast>
        )
      })
    }
  }, [handleAgreed, notified, notify, displayToast, t])

  return (
    <Dialog on={showSettingDialog}>
      {({ getWindowProps }) => (
        <DialogWindow {...getWindowProps()}>
          <DialogHeader>{t('common:cookieNotice.dialog.title')}</DialogHeader>
          <DialogBody>
            <Text
              dangerouslySetInnerHTML={createMarkup(
                t('common:cookieNotice.dialog.description'),
                {
                  renderInline: true,
                }
              )}
            />
            <Option>
              <OptionText>
                <Text>{t('common:cookieNotice.dialog.functional.title')}</Text>
                <SmallText>
                  {t('common:cookieNotice.dialog.functional.description')}
                </SmallText>
              </OptionText>
              <DisabledToggle defaultOn disabled />
            </Option>
            <Option>
              <OptionText>
                <Text>{t('common:cookieNotice.dialog.analytical.title')}</Text>
                <SmallText>
                  {t('common:cookieNotice.dialog.analytical.description')}
                </SmallText>
              </OptionText>
              <Toggle
                defaultOn
                onToggle={() => {
                  if (agreedTo.includes(CONSENT_COOKIE_ANALYTICAL)) {
                    return setAgreedTo([CONSENT_COOKIE_FUNCTIONAL])
                  }

                  setAgreedTo([
                    CONSENT_COOKIE_FUNCTIONAL,
                    CONSENT_COOKIE_ANALYTICAL,
                  ])
                }}
              />
            </Option>
          </DialogBody>
          <DialogFooter>
            <Button
              size="small"
              width="full"
              onClick={() => {
                handleAgreed()
                setShowSettingDialog(false)
              }}
            >
              {t('common:generic.saveAndContinue')}
            </Button>
          </DialogFooter>
        </DialogWindow>
      )}
    </Dialog>
  )
}

CookieToast.propTypes = {
  t: PropTypes.func.isRequired,
}

export default CookieToast
